/** Queue ages cross machines; wall-clock timestamps must not be subtracted
 * from the browser's Date.now(). An HTTP request bounds the relay's clock at
 * receipt by its full round trip (including body decoding). Push can then be
 * aged against that bound, even when a buffered frame arrives late.
 *
 * The local epoch is only a UI-compatible number. Elapsed time is monotonic.
 * No calibration means no trusted queue; order existence is independent. */
export class QueueReceiptClock {
  generation = 0;
  private origin: { wall: number; mono: number } | null = null;
  private anchor: { reported: number; upper: number; at: number } | null = null;
  private observations = new Map<string, { source: number; confirmed: number }>();

  now(): number {
    const mono = performance.now();
    this.origin ??= { wall: Date.now(), mono };
    return this.origin.wall + mono - this.origin.mono;
  }

  reset(): void {
    this.origin = null;
    this.invalidate();
  }

  invalidate(): void {
    this.generation += 1;
    this.anchor = null;
    this.observations.clear();
  }

  transportAge(reported: number | null, requestStarted?: number): number {
    const now = this.now();
    if (reported === null || !Number.isFinite(reported)) return Infinity;
    if (requestStarted !== undefined && Number.isFinite(requestStarted) && requestStarted <= now) {
      const upper = reported + now - requestStarted;
      if (!this.anchor) this.anchor = { reported, upper, at: now };
      else if (reported > this.anchor.reported) {
        // A newer response may tighten the RTT bound. An identical/older
        // replay must NEVER recalibrate the clock to its arrival time.
        const previousUpper = this.anchor.upper + now - this.anchor.at;
        this.anchor = { reported, upper: reported > previousUpper ? upper : Math.min(upper, previousUpper), at: now };
      }
    }
    if (!this.anchor) return Infinity;
    const age = this.anchor.upper + now - this.anchor.at - reported;
    // A clock-domain discontinuity requires a new connection/request bound;
    // don't silently label a future publication current.
    return age >= 0 ? age : Infinity;
  }

  confirm(id: string, source: number, candidate: number): number | null {
    if (!Number.isFinite(candidate)) return null;
    const prior = this.observations.get(id);
    if (prior && source < prior.source) return null;
    const confirmed = prior?.source === source ? Math.min(prior.confirmed, candidate) : candidate;
    this.observations.set(id, { source, confirmed });
    return confirmed;
  }

  retain(ids: ReadonlySet<string>): void {
    for (const id of this.observations.keys()) if (!ids.has(id)) this.observations.delete(id);
  }
}
